const and = (...predicates) => {
  return (traffic) => {
    return predicates.every((predicate) => {
      return predicate(traffic);
    });
  };
};

const not = (predicate) => {
  return (traffic) => {
    return !predicate(traffic);
  };
};

const or = (...predicates) => {
  return (traffic) => {
    return predicates.some((predicate) => {
      return predicate(traffic);
    });
  };
};

class URLParser {
  constructor(url) {
    const modifiedURL = url.toLowerCase().replace(/.*:\/\//, 'http://');
    const urlObject = new URL(modifiedURL);
    this.hostname = urlObject.hostname;
    this.pathname = urlObject.pathname.slice(1);
    this.searchParams = urlObject.searchParams;
  }
}

const pathnameIncludesAfterKey = (key) => {
  return (traffic) => {
    const urlObject = new URLParser(traffic.url);
    const pathnameParts = urlObject.pathname.split('/');
    const keyIndex = pathnameParts.indexOf(key);
    return keyIndex !== -1 && keyIndex !== pathnameParts.length - 1;
  };
};

const pathnameIncludesKey = (pathParam) => {
  return (traffic) => {
    const urlObject = new URLParser(traffic.url);
    const pathParts = urlObject.pathname.split('/');
    return pathParts.includes(pathParam);
  };
};

const referrerExists = () => {
  return (traffic) => {
    return traffic.referrer !== '';
  };
};

const SOCIAL_PLATFORMS = {
  FACEBOOK: 'facebook',
  TWITTER: 'twitter',
  INSTAGRAM: 'instagram',
  REDDIT: 'reddit',
};

const SEARCH_PLATFORMS = {
  GOOGLE: 'google',
  BING: 'bing',
  YAHOO: 'yahoo',
};

const PLATFORM_REGEXES = {
  [SOCIAL_PLATFORMS.FACEBOOK]: [/^facebook\./, /\.facebook\./],
  [SOCIAL_PLATFORMS.TWITTER]: [/^twitter\./, /\.twitter\./, /^t\.co/],
  [SOCIAL_PLATFORMS.INSTAGRAM]: [/^instagram\./, /\.instagram\./],
  [SOCIAL_PLATFORMS.REDDIT]: [/^reddit\./, /\.reddit\./],
  [SEARCH_PLATFORMS.GOOGLE]: [
    /^google\./,
    /\.google\./,
    /googleapi/,
    /googlequicksearchbox/,
    /amp-12up-com/,
    /amp\.12up\.com/,
  ],
  [SEARCH_PLATFORMS.BING]: [/^bing\./, /\.bing\./],
  [SEARCH_PLATFORMS.YAHOO]: [/^yahoo\./, /\.yahoo\./],
};

const TRAFFIC_SOURCES = {
  UNKNOWN: 'unknown',
  INFLUENCER: 'influencer',
  APP: 'app',
  EMBED_PARTNER: 'embed_partner',
  SYNDICATION: 'syndication',
  PAID: 'paid',
  SOCIAL: 'social',
  PARTNER: 'tr_partner',
  ORGANIC: 'organic',
  NAVIGATION: 'navigation',
  RSS: 'rss',
  SHARE: 'share',
  EMAIL: 'email',
  REFERRAL: 'referral',
  DIRECT: 'direct',
};

const TRAFFIC_INFO_KEY = 'trafficInfo';

const referrerIncludesPlatform = (platform) => {
  return (traffic) => {
    if (traffic.referrer === '') {
      return false;
    }
    const referrerObject = new URLParser(traffic.referrer);
    const { hostname } = referrerObject;
    return PLATFORM_REGEXES[platform].some(regex => regex.test(hostname));
  };
};

const referrerIsSameAsSite = () => {
  return (traffic) => {
    if (traffic.referrer === '') {
      return false;
    }
    const urlObject = new URLParser(traffic.url);
    const referrerObject = new URLParser(traffic.referrer);
    return urlObject.hostname === referrerObject.hostname;
  };
};

const searchParamEqualsValue = (searchParam, expectedValue) => {
  return (traffic) => {
    const urlObject = new URLParser(traffic.url);
    const value = urlObject.searchParams.get(searchParam);
    return value === expectedValue;
  };
};

const searchParamsIncludeKey = (searchParam) => {
  return (traffic) => {
    const urlObject = new URLParser(traffic.url);
    const value = urlObject.searchParams.get(searchParam);
    return value !== null;
  };
};

const searchParamIncludesValue = (searchParam, expectedValue) => {
  return (traffic) => {
    const urlObject = new URLParser(traffic.url);
    const value = urlObject.searchParams.get(searchParam);
    return value !== null && value.includes(expectedValue);
  };
};

const predicates = {
  and,
  not,
  or,
  pathnameIncludesAfterKey,
  pathnameIncludesKey,
  referrerExists,
  referrerIncludesPlatform,
  referrerIsSameAsSite,
  searchParamEqualsValue,
  searchParamsIncludeKey,
  searchParamIncludesValue,
};

const searchParamValue = (traffic, key) => {
  const urlObject = new URLParser(traffic.url);
  return urlObject.searchParams.get(key);
};

const afterPathnameKey = (traffic, key) => {
  const urlObject = new URLParser(traffic.url);
  const pathnameParts = urlObject.pathname.split('/');
  const keyIndex = pathnameParts.indexOf(key);
  if (keyIndex === -1 || keyIndex === pathnameParts.length - 1) {
    return null;
  }
  return pathnameParts[keyIndex + 1];
};

const referrerHostname = (traffic) => {
  if (traffic.referrer === '') {
    return '';
  }
  const referrerObject = new URLParser(traffic.referrer);
  const { hostname } = referrerObject;
  const match = Object.entries(PLATFORM_REGEXES).find(
    ([, regexes]) => regexes.some(regex => regex.test(hostname)),
  );
  if (match) {
    const [platform] = match;
    return platform;
  }
  return hostname;
};

const selectors = {
  searchParamValue,
  afterPathnameKey,
  referrerHostname,
};

const influencer = {
  rule: predicates.or(
    predicates.searchParamsIncludeKey('a_aid'),
    predicates.and(
      predicates.pathnameIncludesKey('partners'),
      predicates.pathnameIncludesAfterKey('partners'),
    ),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.INFLUENCER;
    const trafficId = selectors.searchParamValue(traffic, 'a_aid')
      || selectors.afterPathnameKey(traffic, 'partners');

    return {
      trafficSource,
      trafficId,
    };
  },
};

const app = {
  rule: predicates.searchParamEqualsValue('app', '1'),
  extractor: () => {
    return {
      trafficSource: TRAFFIC_SOURCES.APP,
      trafficId: null,
    };
  },
};

const embedPartner = {
  rule: predicates.and(
    predicates.pathnameIncludesKey('embed'),
    predicates.searchParamsIncludeKey('partner'),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.EMBED_PARTNER;
    const trafficId = selectors.searchParamValue(traffic, 'partner');

    return {
      trafficSource,
      trafficId,
    };
  },
};

const syndication = {
  rule: predicates.and(
    predicates.pathnameIncludesKey('synd'),
    predicates.searchParamsIncludeKey('partner'),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.SYNDICATION;
    const trafficId = selectors.searchParamValue(traffic, 'partner');

    return {
      trafficSource,
      trafficId,
    };
  },
};

const paid = {
  rule: predicates.and(
    predicates.searchParamsIncludeKey('utm_source'),
    predicates.or(
      predicates.searchParamEqualsValue('utm_medium', 'fbads'),
      predicates.searchParamEqualsValue('utm_medium', 'cpc'),
      predicates.searchParamEqualsValue('utm_medium', 'fbads_dyn'),
      predicates.searchParamEqualsValue('utm_source', 'googlecpc'),
      predicates.searchParamEqualsValue('utm_source', 'cpc'),
    ),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.PAID;
    const trafficId = selectors.searchParamValue(traffic, 'utm_source');

    return {
      trafficSource,
      trafficId,
    };
  },
};

const social = {
  rule: predicates.or(
    ...Object.values(SOCIAL_PLATFORMS).map(platform => predicates.referrerIncludesPlatform(platform)),
    predicates.searchParamEqualsValue('utm_medium', 'fan_page'),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.SOCIAL;
    const trafficId = selectors.referrerHostname(traffic);

    return {
      trafficSource,
      trafficId,
    };
  },
};

const trPartner = {
  rule: predicates.and(
    predicates.searchParamEqualsValue('utm_medium', 'tr_partner'),
    predicates.searchParamsIncludeKey('utm_source'),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.PARTNER;
    const trafficId = selectors.searchParamValue(traffic, 'utm_source');

    return {
      trafficSource,
      trafficId,
    };
  },
};

const organic = {
  rule: predicates.or(
    ...Object.values(SEARCH_PLATFORMS).map(platform => predicates.referrerIncludesPlatform(platform)),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.ORGANIC;
    const trafficId = selectors.referrerHostname(traffic);

    return {
      trafficSource,
      trafficId,
    };
  },
};

const navigation = {
  rule: predicates.referrerIsSameAsSite(),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.NAVIGATION;
    const trafficId = selectors.referrerHostname(traffic);

    return {
      trafficSource,
      trafficId,
    };
  },
};

const rss = {
  rule: predicates.searchParamEqualsValue('utm_source', 'rss'),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.RSS;
    const trafficId = selectors.referrerHostname(traffic);

    return {
      trafficSource,
      trafficId,
    };
  },
};

const share = {
  rule: predicates.and(
    predicates.searchParamIncludesValue('utm_medium', 'share'),
    predicates.searchParamsIncludeKey('utm_source'),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.SHARE;
    const trafficId = selectors.searchParamValue(traffic, 'utm_source');

    return {
      trafficSource,
      trafficId,
    };
  },
};

const email = {
  rule: predicates.and(
    predicates.searchParamIncludesValue('utm_medium', 'email'),
    predicates.searchParamsIncludeKey('utm_source'),
  ),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.EMAIL;
    const trafficId = selectors.searchParamValue(traffic, 'utm_source');

    return {
      trafficSource,
      trafficId,
    };
  },
};

const referral = {
  rule: predicates.referrerExists(),
  extractor: (traffic) => {
    const trafficSource = TRAFFIC_SOURCES.REFERRAL;
    const trafficId = selectors.referrerHostname(traffic);

    return {
      trafficSource,
      trafficId,
    };
  },
};

const direct = {
  rule: predicates.and(
    predicates.not(predicates.referrerExists()),
    predicates.not(
      predicates.or(
        predicates.searchParamsIncludeKey('utm_medium'),
        predicates.searchParamsIncludeKey('utm_source'),
      ),
    ),
  ),
  extractor: () => {
    return {
      trafficSource: TRAFFIC_SOURCES.DIRECT,
      trafficId: null,
    };
  },
};

// order of pipelines is important
const pipelines = [
  influencer,
  app,
  embedPartner,
  syndication,
  paid,
  social,
  trPartner,
  organic,
  navigation,
  rss,
  share,
  email,
  referral,
  direct,
];

const check = (rule) => {
  return (traffic) => {
    return rule(traffic);
  };
};

const checkAllRules = (traffic) => {
  const pipeline = pipelines.find((item) => {
    return check(item.rule)(traffic);
  });
  if (pipeline) {
    return pipeline.extractor(traffic);
  }
  return {
    trafficSource: TRAFFIC_SOURCES.UNKNOWN,
    trafficId: null,
  };
};

class LocalStorage {
  static getItem(keyName) {
    return JSON.parse(window.localStorage.getItem(keyName));
  }

  static setItem(keyName, keyValue) {
    return window.localStorage.setItem(keyName, JSON.stringify(keyValue));
  }
}

const analyze = () => {
  const traffic = {
    url: window.location.href,
    referrer: document.referrer,
  };
  const { trafficSource: newSource, trafficId: newId } = checkAllRules(traffic);
  const sessionLength = 30;

  let trafficSource = newSource;
  let trafficId = newId;

  const trafficInfo = LocalStorage.getItem(TRAFFIC_INFO_KEY);
  if (trafficInfo !== null) {
    const { trafficSource: oldSource, trafficId: oldId, timestamp } = trafficInfo;
    const timestampValue = new Date(timestamp).getTime();
    const sessionPassed = new Date(timestampValue + sessionLength * 60000) < new Date();
    const isTrafficSourceNavigation = newSource === TRAFFIC_SOURCES.NAVIGATION;
    if (isTrafficSourceNavigation) {
      if (sessionPassed) {
        trafficSource = TRAFFIC_SOURCES.DIRECT;
        trafficId = null;
      } else {
        trafficSource = oldSource;
        trafficId = oldId;
      }
    }
  }
  LocalStorage.setItem(TRAFFIC_INFO_KEY, { trafficSource, trafficId, timestamp: new Date() });
};

const trafficSourceAnalysis = () => {
  analyze();
  const trafficInfo = LocalStorage.getItem(TRAFFIC_INFO_KEY);
  const { trafficSource, trafficId } = trafficInfo || {};
  const trafficSourceAndId = trafficId ? `${trafficSource}-${trafficId}` : trafficSource;
  return { trafficSource, trafficId, trafficSourceAndId };
};

const trafficSourceInfo = trafficSourceAnalysis();

export { trafficSourceInfo };
//# sourceMappingURL=index.bundle.esm.js.map
